import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User } from '@/features/auth';

// ============================================================================
// TYPES
// ============================================================================

export interface AgentStats {
  totalSales: number;
  monthlySales: number;
  weeklySales: number;
  totalEarnings: number;
  monthlyEarnings: number;
  pendingApplications: number;
  completedTrainings: number;
  totalTrainings: number;
  complianceScore: number;
  lastSaleDate?: string;
  averageSaleValue?: number;
}

export interface DeviceInfo {
  id: string;
  name: string;
  fingerprint: string;
  isActive: boolean;
  lastUsed: string;
  createdAt: string;
}

export interface AgentProfile extends User {
  // Extends User with agent-specific fields
  stats?: AgentStats;
  devices?: DeviceInfo[];
  agentCode?: string;
  licenseNumber?: string;
}

export interface AgentState {
  // State
  agent: AgentProfile | null;
  stats: AgentStats | null;
  devices: DeviceInfo[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;

  // Synchronous Actions
  setAgent: (agent: AgentProfile | null) => void;
  updateAgent: (updates: Partial<AgentProfile>) => void;
  clearAgent: () => void;
  setStats: (stats: AgentStats) => void;
  updateStats: (updates: Partial<AgentStats>) => void;
  setDevices: (devices: DeviceInfo[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Async Actions
  fetchAgentData: () => Promise<void>;
  refreshStats: () => Promise<void>;
  updateProfile: (data: Partial<AgentProfile>) => Promise<void>;
  fetchDevices: () => Promise<void>;
}

// ============================================================================
// STORE
// ============================================================================

export const useAgentStore = create<AgentState>()(
  devtools(
    persist(
      (set, get) => ({
        // ========================================================================
        // INITIAL STATE
        // ========================================================================
        agent: null,
        stats: null,
        devices: [],
        isLoading: false,
        error: null,
        lastUpdated: null,

        // ========================================================================
        // SYNCHRONOUS ACTIONS
        // ========================================================================

        setAgent: (agent) => {
          set({ 
            agent, 
            error: null,
            lastUpdated: Date.now()
          });
        },

        updateAgent: (updates) => {
          const { agent } = get();
          if (!agent) {
            console.warn('⚠️ Agent Store: Cannot update - no agent set');
            return;
          }
          set({ 
            agent: { ...agent, ...updates },
            lastUpdated: Date.now()
          });
        },

        clearAgent: () => {
          set({
            agent: null,
            stats: null,
            devices: [],
            error: null,
            lastUpdated: null
          });
        },

        setStats: (stats) => {
          set({ 
            stats, 
            error: null,
            lastUpdated: Date.now()
          });
        },

        updateStats: (updates) => {
          const { stats } = get();
          if (!stats) {
            console.warn('⚠️ Agent Store: Cannot update stats - no stats set');
            return;
          }
          set({ 
            stats: { ...stats, ...updates },
            lastUpdated: Date.now()
          });
        },

        setDevices: (devices) => {
          set({ devices });
        },

        setLoading: (isLoading) => {
          set({ isLoading });
        },

        setError: (error) => {
          console.error('❌ Agent Store Error:', error);
          set({ error, isLoading: false });
        },

        // ========================================================================
        // ASYNC ACTIONS
        // ========================================================================

        fetchAgentData: async () => {
          set({ isLoading: true, error: null });

          try {
            // Get user from localStorage (set by AuthContext)
            const userStr = localStorage.getItem('user');
            if (!userStr) {
              throw new Error('No user found in localStorage');
            }

            const user: User = JSON.parse(userStr);
            
            // Set basic agent profile
            set({ 
              agent: user as AgentProfile,
              isLoading: false,
              lastUpdated: Date.now()
            });


            // Fetch stats in background
            get().refreshStats();
          } catch (error: any) {
            console.error('❌ Agent Store: Failed to fetch agent data', error);
            set({
              error: error.message || 'Failed to load agent data',
              isLoading: false
            });
          }
        },

        refreshStats: async () => {
          
          try {
            const { AgentService } = await import('../services');
            
            // Fetch sales reports to calculate stats
            // Note: This endpoint may not exist yet, so we'll handle errors gracefully
            let salesReports: any[] = [];
            try {
              salesReports = await AgentService.getSalesReports();
            } catch (apiError: any) {
              // If the endpoint doesn't exist or returns 404, use empty array
              // This prevents login from failing due to missing endpoint
              if (apiError?.response?.status === 404 || apiError?.message?.includes('not found')) {
                console.warn('⚠️ Agent Store: Sales reports endpoint not available, using empty stats');
                salesReports = [];
              } else {
                // Re-throw other errors
                throw apiError;
              }
            }
            
            // Calculate stats from sales data
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const currentWeek = getWeekNumber(now);

            const monthlySales = salesReports.filter(report => {
              const reportDate = new Date(report.reportDate);
              return reportDate.getMonth() === currentMonth && 
                     reportDate.getFullYear() === currentYear;
            }).length;

            const weeklySales = salesReports.filter(report => {
              const reportDate = new Date(report.reportDate);
              return getWeekNumber(reportDate) === currentWeek && 
                     reportDate.getFullYear() === currentYear;
            }).length;

            const stats: AgentStats = {
              totalSales: salesReports.length,
              monthlySales,
              weeklySales,
              totalEarnings: 0, // Would need pay reports for this
              monthlyEarnings: 0,
              pendingApplications: salesReports.filter(r => r.status === 'PENDING').length,
              completedTrainings: 0, // Would need training data
              totalTrainings: 0,
              complianceScore: 100,
              lastSaleDate: salesReports[0]?.reportDate
            };

            set({ 
              stats,
              lastUpdated: Date.now()
            });

          } catch (error: any) {
            console.error('❌ Agent Store: Failed to refresh stats', error);
            // Don't set error state for background refresh
            // Set default stats on error
            set({
              stats: {
                totalSales: 0,
                monthlySales: 0,
                weeklySales: 0,
                totalEarnings: 0,
                monthlyEarnings: 0,
                pendingApplications: 0,
                completedTrainings: 0,
                totalTrainings: 0,
                complianceScore: 100
              },
              lastUpdated: Date.now()
            });
          }
        },

        updateProfile: async (data) => {
          set({ isLoading: true, error: null });

          try {
            const { agent } = get();
            if (!agent) {
              throw new Error('No agent profile to update');
            }

            // TODO: Import user service when available
            // const { UserService } = await import('../services/userService');
            // const updatedAgent = await UserService.updateProfile(agent.id, data);

            // For now, just update local state
            const updatedAgent = { ...agent, ...data };

            // Update local state
            set({
              agent: updatedAgent,
              isLoading: false,
              lastUpdated: Date.now()
            });

            // Update localStorage
            localStorage.setItem('user', JSON.stringify(updatedAgent));

          } catch (error: any) {
            console.error('❌ Agent Store: Failed to update profile', error);
            set({
              error: error.message || 'Failed to update profile',
              isLoading: false
            });
            throw error;
          }
        },

        fetchDevices: async () => {

          try {
            // TODO: Import device service when available
            // const { DeviceService } = await import('../services/deviceService');
            // const devices = await DeviceService.getDevices();
            
            // For now, return empty array
            const devices: DeviceInfo[] = [];
            
            set({ devices });

          } catch (error: any) {
            console.error('❌ Agent Store: Failed to fetch devices', error);
            // Don't set error for non-critical device fetch
          }
        },
      }),
      {
        name: 'agent-storage',
        partialize: (state) => ({
          agent: state.agent,
          stats: state.stats,
          lastUpdated: state.lastUpdated
        }),
      }
    ),
    { name: 'AgentStore' }
  )
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getWeekNumber(date: Date): number {
  const onejan = new Date(date.getFullYear(), 0, 1);
  const millisecsInDay = 86400000;
  return Math.ceil((((date.getTime() - onejan.getTime()) / millisecsInDay) + onejan.getDay() + 1) / 7);
}

